/**
 * Übersicht: Kacheln, Monatsverlauf, Auftraggeber, auslaufende Projekte,
 * Leistungsarten.
 */
import React, { useMemo } from 'react';
import { BarChart3, CalendarClock, ChevronRight, Crown, Layers } from 'lucide-react';
import type { CockpitModel, ProjectRow, ServiceTypeRow } from '../../services/cockpitTypes';
import { Bar, Panel } from './CockpitBits';
import { KpiTiles } from './KpiTiles';
import { ChartLegend, RevenueChart } from './RevenueChart';
import { fmtCents, fmtCentsShort, pctOf } from './cockpitUi';

export interface OverviewTabProps {
    model: CockpitModel;
    hideAmounts: boolean;
    periodText: string;
    todayMonth: number | null;
    onSelectMonth: (index: number) => void;
    onSelectProject: (project: ProjectRow) => void;
    onSelectServiceType: (type: ServiceTypeRow) => void;
}

export const OverviewTab: React.FC<OverviewTabProps> = ({
    model, hideAmounts, periodText, todayMonth, onSelectMonth, onSelectProject, onSelectServiceType,
}) => {
    const expiring = useMemo(
        () => model.projects
            .filter((p) => p.status === 'active' && p.daysRemaining !== null && p.daysRemaining > 0 && p.daysRemaining <= 90)
            .sort((a, b) => (a.daysRemaining ?? 0) - (b.daysRemaining ?? 0)),
        [model.projects],
    );

    const serviceTypes = useMemo(
        () => [...model.serviceTypes].sort((a, b) => b.forecastCents - a.forecastCents),
        [model.serviceTypes],
    );

    const topCustomer = model.customers[0]?.performedCents ?? 1;

    // Der Vormonatsvergleich ergibt nur bei Zeitraum „Monat" einen Sinn. Im
    // Januar ist der Vormonat der Dezember des VORJAHRES — dafuer haelt das
    // Modell `prevDecember` bereit.
    const prevPerformedCents = useMemo(() => {
        if (model.period.kind !== 'month') return null;
        const index = model.period.month - 1;
        return index === 0
            ? model.prevDecember.performedCents
            : model.months[index - 1]?.performedCents ?? 0;
    }, [model.period, model.months, model.prevDecember]);

    return (
        <div data-glass-view="cockpit" className="space-y-5">
            <KpiTiles
                tiles={model.tiles}
                periodText={periodText}
                hideAmounts={hideAmounts}
                prevPerformedCents={prevPerformedCents}
            />

            <Panel
                title="Monatsverlauf"
                icon={<BarChart3 className="w-4 h-4 text-blue-500" />}
                right={<ChartLegend />}
            >
                <RevenueChart
                    model={model}
                    hideAmounts={hideAmounts}
                    todayMonth={todayMonth}
                    onSelectMonth={onSelectMonth}
                />
                <p className="text-[11px] text-slate-400 mt-2">
                    Monat anklicken für die Belege und Leistungen dahinter. Monate außerhalb des gewählten
                    Zeitraums sind gedämpft, zählen aber nicht in die Kacheln.
                </p>
            </Panel>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 items-start">
                <Panel title="Top Auftraggeber" icon={<Crown className="w-4 h-4 text-amber-500" />}>
                    <div className="space-y-3">
                        {model.customers.length === 0 && <div className="py-6 text-center text-slate-400 text-[11px]">Keine Leistungen im Zeitraum</div>}
                        {model.customers.slice(0, 6).map((c) => (
                            <div key={c.name}>
                                <div className="flex justify-between gap-2 mb-1">
                                    <span className="text-xs text-slate-700 truncate">{c.name}</span>
                                    <span className="text-xs font-semibold text-slate-700 shrink-0">{fmtCentsShort(c.performedCents, hideAmounts)}</span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <Bar value={c.performedCents} max={topCustomer} color="bg-amber-400" />
                                    <span className="text-[11px] text-slate-400 shrink-0">{c.projectCount}P</span>
                                </div>
                            </div>
                        ))}
                    </div>
                </Panel>

                <Panel title="Auslaufend" icon={<CalendarClock className="w-4 h-4 text-red-500" />} hint="≤ 90 Tage">
                    {expiring.length === 0 ? (
                        <div className="py-6 text-center text-slate-400 text-[11px]">Keine aktiven Projekte in ≤ 90 Tagen</div>
                    ) : (
                        <div className="space-y-2">
                            {expiring.slice(0, 6).map((p) => (
                                <div
                                    key={p.id}
                                    className="flex items-center gap-3 p-2.5 rounded-xl border border-slate-200 cursor-pointer hover:border-blue-300 transition-colors"
                                    onClick={() => onSelectProject(p)}
                                >
                                    <div className={`w-10 h-9 rounded-lg flex items-center justify-center text-[11px] font-semibold shrink-0 border ${(p.daysRemaining ?? 0) <= 30 ? 'bg-red-50 text-red-600 border-red-200' : 'bg-amber-50 text-amber-600 border-amber-200'}`}>
                                        {p.daysRemaining}d
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <div className="text-xs font-semibold text-slate-700 truncate">{p.name}</div>
                                        <div className="text-[11px] text-slate-400 truncate">{p.responsibleName}</div>
                                    </div>
                                    <div className="text-right shrink-0">
                                        <div className="text-xs font-semibold text-slate-600">{fmtCentsShort(p.contractCents, hideAmounts)}</div>
                                        <div className="text-[11px] text-slate-400">{fmtCentsShort(p.backlogCents, hideAmounts)} offen</div>
                                    </div>
                                    <ChevronRight className="w-3.5 h-3.5 text-slate-300 shrink-0" />
                                </div>
                            ))}
                        </div>
                    )}
                </Panel>

                <Panel title="Leistungsarten" icon={<Layers className="w-4 h-4 text-indigo-500" />}>
                    <div className="space-y-3">
                        {serviceTypes.length === 0 && <div className="py-6 text-center text-slate-400 text-[11px]">Keine Leistungsarten im Zeitraum</div>}
                        {serviceTypes.slice(0, 6).map((t) => (
                            <div
                                key={t.key}
                                className="cursor-pointer hover:bg-slate-50 p-2 -mx-2 rounded-xl transition-colors"
                                onClick={() => onSelectServiceType(t)}
                            >
                                <div className="flex justify-between gap-2 mb-1">
                                    <span className="text-xs text-slate-700 truncate">{t.label}</span>
                                    <span className="text-[11px] text-slate-400 shrink-0 flex items-center gap-1">
                                        {t.entryCount} Einträge<ChevronRight className="w-3 h-3 text-slate-300" />
                                    </span>
                                </div>
                                <Bar value={t.performedCents} max={Math.max(t.forecastCents, t.performedCents, 1)} color="bg-emerald-500" height="h-1.5" />
                                <div className="flex justify-between mt-1">
                                    <span className="text-[11px] text-emerald-600">{fmtCents(t.performedCents, hideAmounts)}</span>
                                    <span className="text-[11px] text-slate-400">
                                        {pctOf(t.performedCents, t.forecastCents)}% von {fmtCentsShort(t.forecastCents, hideAmounts)}
                                    </span>
                                </div>
                            </div>
                        ))}
                    </div>
                </Panel>
            </div>
        </div>
    );
};
